//@flow
import React, {useEffect, useRef, useState} from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import {rgxCountries, getFlagByCountryCode} from '../../on-boarding/utils';
import CustomTextField from '../../../elements/CustomTextField/CustomTextField';
import {connect} from 'react-redux';

import type {Country} from '../../../entities';
import type {RootState} from '../..';
import type {InputProps, InputLabelProps} from '@material-ui/core';

export default connect(
  (state: RootState) => ({
    countries: state.country.countries,
  }),
  null
)(ControlledTelInput);
type Props = {
  countries?: Country[],
  label: string,
  variant?: string,
  fullWidth?: boolean,
  name: string,
  error?: boolean,
  helperText?: string,
  inputRef?: any,
  // register: (rules?: ValidationRules) => (ref: Ref | null) => void,
  onChange?: (phone: string, value: string, prefix: string) => void,
  onError?: (error: string | null) => void,
  onInput?: (phone: string) => void,
  defaultValue: ?string,
  defaultCountry?: Country,
  disabled?: boolean,
  className?: string,
  value?: string,

  inputProps: InputProps,
  InputLabelProps: InputLabelProps,
};

const useStyles = makeStyles(theme => ({
  mainWrap: {
    position: 'relative',
  },
  flagIcon: {
    width: 12,
    height: 12,
    marginRight: 6,
  },
  textField: {
    '& label': {
      paddingLeft: theme.spacing(10),
    },
    '& legend': {
      marginLeft: 59,
    },
  },
  select: {
    marginTop: '6px',
    position: 'absolute',
    left: 10,
    top: 5,
    height: 56,

    '& div': {
      backgroundColor: 'unset!important',
    },
  },
  label: {
    marginLeft: '-80px !important',
  },
  input: {
    paddingLeft: theme.spacing(12),
  },
}));

function ControlledTelInput(props: Props) {
  const {
    countries,
    label,
    fullWidth,
    name,
    helperText,
    onChange,
    defaultCountry,
    disabled = false,
    className,
    value: propPhone,
    InputLabelProps,
    inputProps,
    onError,
    ...otherTextFieldProps
  } = props;

  const classes = useStyles();

  const firstPrint = useRef(true);
  const [phone, setPhone] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(
    defaultCountry || {
      code: 'PE',
      phonePrefix: '51',
      name: 'Peru',
    }
  );

  const prefix = selectedCountry?.phonePrefix || '51';

  //$FlowFixMe , to avoid passing dispatch to input from MUIautocomplete
  const {dispatch, ...textFieldProps} = otherTextFieldProps;

  const [error, setError] = useState<string | null>(null);

  const onChangePrefix = e => {
    const countryCode = e.target.value;

    const newSelectedCountry = countries?.find(
      country => country.code === countryCode
    );

    if (newSelectedCountry) {
      setSelectedCountry(newSelectedCountry);
      const prefixValue = newSelectedCountry.phonePrefix;
      const fullPhone = `${prefixValue}${phone}`;
      onChange && onChange(fullPhone, phone, prefixValue);
    }
  };

  const onChangePhone = e => {
    const phoneValue = e.target.value;
    setPhone(phoneValue);
    firstPrint.current = false;
    const fullPhone = `${prefix || ''}${phoneValue}`;
    onChange && onChange(fullPhone, phoneValue, prefix || '');
  };

  const validType = (v: string): boolean => {
    const numberRgx = /^[0-9]*$/;
    return numberRgx.test(v);
  };

  const validFormat = (v: string, prefix: string): boolean => {
    const countrySelected = countries
      ? countries.find(i => i.phonePrefix === prefix)
      : null;

    if (countrySelected && rgxCountries[countrySelected.code])
      return rgxCountries[countrySelected.code].test(v);

    return rgxCountries['DEFAULT'].test(v);
  };

  useEffect(() => {
    function validateFields(v: string, p: string) {
      if (!v) return setError('Campo requerido');
      if (!validType(v)) return setError('Formato de número inválido');
      if (!validFormat(v, p)) return setError('Formato de número inválido');
      return setError(null);
    }

    if (firstPrint.current) return;
    validateFields(phone, prefix || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, prefix]);

  useEffect(() => {
    const getCountryByPrefix = (fullPhone?: string) => {
      if (fullPhone && countries) {
        const country = countries.find(({phonePrefix}) =>
          phonePrefix ? fullPhone.startsWith(phonePrefix) : false
        );
        if (country) return country;
        if (defaultCountry) return defaultCountry;
      }

      return countries?.find(c => c.code === 'PE') || null;
    };

    const getPhone = (fullPhone: string, prefix: string) => {
      if (!prefix) return fullPhone || '';

      const starWithPrefix = fullPhone.startsWith(prefix);

      if (starWithPrefix) return fullPhone.substring(prefix.length);

      return fullPhone;
    };

    if (propPhone) {
      const country = getCountryByPrefix(propPhone);
      const selectedCountryObj = country || {
        code: 'PE',
        phonePrefix: '51',
        name: 'Peru',
      };
      const phone = getPhone(propPhone, selectedCountryObj.phonePrefix || '');

      setSelectedCountry(selectedCountryObj);
      setPhone(phone);
    }
  }, [propPhone, countries, defaultCountry]);

  useEffect(() => {
    if (onError) onError(error);
  }, [error, onError]);

  if (!countries) return null;

  return (
    <div className={`${classes.mainWrap} ${className ? className : ''}`}>
      <CustomTextField
        {...textFieldProps}
        label={<div className={classes.label}>{label}</div>}
        fullWidth={fullWidth}
        name={name}
        error={!!error}
        helperText={helperText ? helperText : error ? error : ''}
        onChange={onChangePhone}
        value={phone}
        inputProps={{...inputProps, className: classes.input, value: phone}}
        className={classes.textField}
        disabled={disabled}
        InputLabelProps={{
          ...InputLabelProps,
          shrink: true,
        }}
      />
      <Select
        labelId="country"
        id="country"
        value={selectedCountry?.code || 'PE'}
        onChange={onChangePrefix}
        className={classes.select}
        disableUnderline
      >
        {countries.map(c => (
          <MenuItem key={c.code} value={c.code}>
            <img
              src={getFlagByCountryCode(c.code)}
              className={classes.flagIcon}
              alt={c.name}
            />
            {selectedCountry?.code !== c.code && c.name} ({c.phonePrefix})
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}
